import React, { useState } from 'react';
import { AlertTriangle, User } from 'lucide-react';
import { approveCustomerOfferRate, declineCustomerOfferRate } from '../../../../../../services/staffingService';
import toast from 'react-hot-toast';

const STATUS_BADGE = {
  accepted:  { bg: 'bg-sky-100',     text: 'text-sky-700',     label: 'Assigned'  },
  completed: { bg: 'bg-gray-100',    text: 'text-gray-600',    label: 'Completed' },
  paid:      { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid'      },
  disputed:  { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'Disputed'  },
  resolved:  { bg: 'bg-purple-100',  text: 'text-purple-700',  label: 'Resolved'  },
};

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

/**
 * OfficerRow — purely presentational row for one assigned officer.
 * Fires approveCustomerOfferRate / declineCustomerOfferRate, then calls onRateAction
 * so the parent (CustomerJobDetail) can re-fetch shiftDetail.
 */
const OfficerRow = ({ offer, customerCompanyId, jobId, onRateAction }) => {
  const user = offer.provider_resource_id?.user_id;
  const name = user ? `${user.firstName} ${user.lastName}` : 'Officer';
  const initials = user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}` : '?';
  const cfg = STATUS_BADGE[offer.status] || STATUS_BADGE.completed;
  const shift = offer.shifts?.[0];
  const needsApproval = offer.negotiations?.actionNeeded && !offer.negotiations?.approval?.customer;

  const [approving, setApproving] = useState(false);
  const [declining, setDeclining] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await approveCustomerOfferRate(customerCompanyId, jobId, offer._id);
      toast.success('Rate approved');
      onRateAction();
    } catch {
      toast.error('Failed to approve rate');
    } finally {
      setApproving(false);
    }
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      await declineCustomerOfferRate(customerCompanyId, jobId, offer._id, 'Rate declined by customer');
      toast.success('Rate declined');
      onRateAction();
    } catch {
      toast.error('Failed to decline rate');
    } finally {
      setDeclining(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#404880]/10 text-[#404880] text-sm font-bold">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">{name}</p>
          {offer.provider_company_id?.agency_name && (
            <p className="text-xs text-muted-foreground truncate">{offer.provider_company_id.agency_name}</p>
          )}
        </div>
        <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
          {cfg.label}
        </span>
      </div>



      {/* Check-in / check-out */}
      {shift && (
        <div className="grid grid-cols-2 gap-2">
          <div className={`rounded-lg p-2.5 ${shift.checkIn?.confirmed ? 'bg-emerald-50' : 'bg-muted'}`}>
            <p className="text-[10px] font-semibold text-muted-foreground mb-0.5">CHECK IN</p>
            <p className={`text-xs font-bold ${shift.checkIn?.confirmed ? 'text-emerald-700' : 'text-muted-foreground'}`}>
              {shift.checkIn?.confirmed ? fmt(shift.checkIn.timestamp) : 'Not yet'}
            </p>
          </div>
          <div className={`rounded-lg p-2.5 ${shift.checkOut?.confirmed ? 'bg-emerald-50' : 'bg-muted'}`}>
            <p className="text-[10px] font-semibold text-muted-foreground mb-0.5">CHECK OUT</p>
            <p className={`text-xs font-bold ${shift.checkOut?.confirmed ? 'text-emerald-700' : 'text-muted-foreground'}`}>
              {shift.checkOut?.confirmed ? fmt(shift.checkOut.timestamp) : 'Not yet'}
            </p>
          </div>
        </div>
      )}

      {/* Rate approval banner */}
      {needsApproval && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-800">Rate Approval Needed</p>
              <p className="text-xs text-amber-700">
                Officer requests <strong>${offer.negotiations.providerHourlyRate}/hr</strong>
                {offer.negotiations.expiredAt && (
                  <> · expires {new Date(offer.negotiations.expiredAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleApprove}
              disabled={approving}
              className="flex-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors"
            >
              {approving ? 'Approving…' : 'Approve'}
            </button>
            <button
              onClick={handleDecline}
              disabled={declining}
              className="flex-1 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60 transition-colors"
            >
              {declining ? 'Declining…' : 'Decline'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * OfficersTab — pure presentational. Renders the list of offers for a shift.
 *
 * Props:
 *   offers[]         — from shiftDetail.offers (fetched by CustomerJobDetail)
 *   filledCount      — from shiftDetail.filledCount
 *   customerCompanyId
 *   jobId
 *   onRateAction()   — callback → CustomerJobDetail.fetchShiftDetail (re-fetch)
 */
const OfficersTab = ({ offers, filledCount, customerCompanyId, jobId, onRateAction }) => {
  if (offers.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border py-12 text-center">
        <User size={28} className="mx-auto mb-2 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No officers assigned yet</p>
        <p className="text-xs text-muted-foreground/60 mt-1">The staffing agency will assign officers soon</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {offers.map((offer) => (
        <OfficerRow
          key={offer._id}
          offer={offer}
          customerCompanyId={customerCompanyId}
          jobId={jobId}
          onRateAction={onRateAction}
        />
      ))}
    </div>
  );
};

export default OfficersTab;
